/**
 * API client for Blyrha backend
 * Handles communication with the PHP backend API
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

interface ApiResponse<T> {
  data?: T;
  error?: string;
  details?: Record<string, string>;
  status: number;
}

interface EmailTokenResponse {
  token: string;
  expires_in: number;
}

interface EmailRevealResponse {
  email: string;
}

interface ContactFormResponse {
  success: boolean;
  message: string;
}

interface DemoRequestPayload {
  selectedOption: 'demo' | 'trial';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  companySize: string;
  role: string;
  currentTools: string;
  useCase: string;
  preferredDate: string;
  preferredTime: string;
  goals: string;
  newsletter: boolean;
}

interface DemoRequestResponse {
  success: boolean;
  message: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Request an email reveal token
   * @param emailId - The email identifier (e.g., 'support', 'legal', 'contact')
   * @returns Token and expiration time
   */
  async requestEmailToken(emailId: string): Promise<ApiResponse<EmailTokenResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/email/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: emailId }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          error: data.error || 'Failed to request email token',
          status: response.status,
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (error) {
      console.error('Error requesting email token:', error);
      return {
        error: 'Network error. Please check your connection.',
        status: 0,
      };
    }
  }

  async submitDemoRequest(
    formData: DemoRequestPayload,
  ): Promise<ApiResponse<DemoRequestResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          error: data.error || 'Failed to submit demo request',
          details: data.details,
          status: response.status,
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (error) {
      console.error('Error submitting demo request:', error);
      return {
        error: 'Network error. Please check your connection.',
        status: 0,
      };
    }
  }

  /**
   * Reveal an email address using a token
   * @param token - The email reveal token
   * @returns The email address
   */
  async revealEmail(token: string): Promise<ApiResponse<EmailRevealResponse>> {
    try {
      // URL-encode the token to handle special characters like / and +
      const encodedToken = encodeURIComponent(token);
      const response = await fetch(`${this.baseUrl}/email/reveal/${encodedToken}`);
      const data = await response.json();

      if (!response.ok) {
        return {
          error: data.error || 'Failed to reveal email',
          status: response.status,
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (error) {
      console.error('Error revealing email:', error);
      return {
        error: 'Network error. Please check your connection.',
        status: 0,
      };
    }
  }

  /**
   * Submit a contact form
   * @param formData - Contact form data
   * @returns Success response
   */
  async submitContactForm(formData: {
    name: string;
    email: string;
    subject: string;
    message: string;
    company?: string;
    website?: string;
  }): Promise<ApiResponse<ContactFormResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          error: data.error || 'Failed to submit contact form',
          details: data.details,
          status: response.status,
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (error) {
      console.error('Error submitting contact form:', error);
      return {
        error: 'Network error. Please check your connection.',
        status: 0,
      };
    }
  }

  /**
   * Check API health
   * @returns Health status
   */
  async health(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return response.ok;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }
}

export const apiClient = new ApiClient();
